/**
 * Utility functions for handling pricing based on user groups
 */

export interface User {
  id: number;
  username: string;
  name: string;
  surname: string;
  company: string;
  erp_id: number;
  country: string;
  city: string;
  email: string;
  password: string;
  phone: string;
  address: string;
  is_active: boolean;
  is_admin: boolean;
  is_client: boolean;
  activate_grid_view: boolean;
  zone: string;
  group?: string;
}

export interface ArticlePrice {
  CMSH: number;
  CMSH1: number;
  CMSH2: number;
  CMSH3: number;
  CMSH4: number;
  CMSH5: number;
  CMSH6: number;
  CMSH7: number;
  CMSH8: number;
  CMSH9: number;
  CMSH10: number;
  CMSH11: number | null;
  CMSH12: number;
  CMSH13: number;
  CMSH14: number;
  CMSH15: number;
  CMSH16: number;
  CMSH17: number;
  CMSH18: number;
  CMSH19: number;
}

/**
 * Get the current authenticated user from localStorage
 */
export const getCurrentUser = (): User | null => {
  try {
    const userStr = localStorage.getItem("user");
    if (userStr) {
      return JSON.parse(userStr);
    }
    return null;
  } catch (error) {
    console.error("Error parsing user from localStorage:", error);
    return null;
  }
};

/**
 * Get the user's group for pricing purposes
 */
export const getUserGroup = (user: User | null): string => {
  return user?.group || 'A';
};

/**
 * Get the price field name based on user group
 */
export const getPriceFieldByGroup = (group: string): keyof ArticlePrice => {
  const code = (group || 'A').toUpperCase();
  switch (code) {
    case 'A': return 'CMSH';
    case 'B': return 'CMSH1';
    case 'C': return 'CMSH2';
    case 'D': return 'CMSH3';
    case 'E': return 'CMSH4';
    case 'F': return 'CMSH5';
    case 'G': return 'CMSH6';
    case 'H': return 'CMSH7';
    case 'I': return 'CMSH8' as keyof ArticlePrice; // If CMSH8 exists; fallback handled below
    default: return 'CMSH';
  }
};

/**
 * Get the retail price field name based on user group
 * For all groups, we use the same field as the main price
 */
export const getRetailPriceFieldByGroup = (group: string): keyof ArticlePrice => {
  return getPriceFieldByGroup(group);
};

/**
 * Get the appropriate price for the current user based on their group
 */
export const getUserPrice = (article: ArticlePrice): number => {
  const user = getCurrentUser();
  const userGroup = getUserGroup(user);
  const priceField = getPriceFieldByGroup(userGroup);
  const price = article[priceField];
  
  // Return the price if it exists and is greater than 0, otherwise fallback to CMSH
  if (price && price > 0) {
    return price;
  }
  
  // Fallback to base price (CMSH)
  return article.CMSH || 0;
};

/**
 * Get the retail price for the current user based on their group
 * For the new logic, retail price is the same as the main price
 */
export const getUserRetailPrice = (article: ArticlePrice): number => {
  const user = getCurrentUser();
  const userGroup = getUserGroup(user);
  const priceField = getRetailPriceFieldByGroup(userGroup);
  const price = article[priceField];
  
  // Return the price if it exists and is greater than 0, otherwise fallback to CMSH
  if (price && price > 0) {
    return price;
  }
  
  // Fallback to base price (CMSH)
  return article.CMSH || 0;
};

/**
 * Get the display name for a group
 */
export const getGroupDisplayName = (group: string): string => {
  const displayNames: Record<string, string> = {
    'A': 'Standard',
    'B': 'Silver',
    'C': 'Gold',
    'D': 'Platinum',
    'E': 'Premium',
    'F': 'VIP',
    'G': 'Elite',
    'H': 'Diamond',
    'I': 'Ruby',
    'J': 'Sapphire'
  };
  
  return displayNames[group.toUpperCase()] || 'Standard';
};

/**
 * Get the currency for a user group
 * A, F, I -> LEK
 * B, C, D, E, G, H -> EUR
 * Default -> EUR
 */
export const getCurrencyByGroup = (group: string): string => {
  const code = (group || '').toUpperCase();
  const lekGroups = new Set(['A','F','I']);
  const eurGroups = new Set(['B','C','D','E','G','H']);
  if (lekGroups.has(code)) return 'LEK';
  if (eurGroups.has(code)) return 'EUR';
  return 'EUR';
};

/**
 * Get the currency symbol for a user group
 */
export const getCurrencySymbolByGroup = (group: string): string => {
  return getCurrencyByGroup(group) === 'EUR' ? '€' : 'LEK';
};

/**
 * Get the currency for the current user
 */
export const getCurrentUserCurrency = (): string => {
  const user = getCurrentUser();
  return getCurrencyByGroup(getUserGroup(user));
};

/**
 * Get the currency symbol for the current user
 */
export const getCurrentUserCurrencySymbol = (): string => {
  return getCurrencySymbolByGroup(getUserGroup(getCurrentUser()));
};

/**
 * Format price with currency based on group mapping
 */
export const formatPriceWithCurrency = (price: number | string | undefined | null, groupOrCurrency?: string): string => {
  // Handle undefined, null, or invalid price values
  let numericPrice = 0;
  
  if (price === undefined || price === null) {
    numericPrice = 0;
  } else if (typeof price === 'string') {
    numericPrice = parseFloat(price) || 0;
  } else if (typeof price === 'number') {
    numericPrice = price;
  } else {
    numericPrice = 0;
  }
  
  // Ensure it's a valid number
  if (isNaN(numericPrice)) {
    numericPrice = 0;
  }

  const code = (groupOrCurrency || '').toUpperCase();
  let currency: string;
  if (code === 'EUR' || code === 'LEK') {
    currency = code;
  } else {
    const effectiveGroup = groupOrCurrency || getUserGroup(getCurrentUser());
    currency = getCurrencyByGroup(effectiveGroup);
  }

  if (currency === 'EUR') {
    return `${numericPrice.toFixed(2)} €`;
  }
  return `${numericPrice.toFixed(2)} LEK`;
}; 